import { createFileRoute, ClientOnly } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

// recharts (~107 KB gzip) loads only on the client, only on this internal page.
const RankTrendCharts = lazy(() => import("@/components/RankTrendCharts"));

const CHART_PLACEHOLDER = (
  <div className="rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground">
    กำลังโหลดกราฟ…
  </div>
);

/**
 * Sprint 2 Rank Dashboard — tracks 3 quick-win pages weekly to T+30.
 * Data source: manual weekly snapshots from Google Search Console.
 * Once GSC OAuth is connected, `/api/public/rank-snapshot` will auto-populate.
 *
 * `noindex` — internal tracking page, not for public SEO.
 */
export const Route = createFileRoute("/tools/rank-dashboard")({
  head: () => ({
    meta: [
      { title: "Rank Dashboard — T+30 Quick-Win Tracker (Internal)" },
      { name: "description", content: "Internal weekly rank tracker for 3 quick-win pages through T+30." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: RankDashboard,
});

type Snapshot = { week: string; position: number | null; impressions: number; ctr: number };

type TrackedPage = {
  slug: string;
  label: string;
  url: string;
  keyword: string;
  history: Snapshot[];
};

// Weekly snapshots — update every Monday. `null` position = not yet ranked (>100).
const PAGES: TrackedPage[] = [
  {
    slug: "notary-apostille-poa",
    label: "Apostille POA",
    url: "/services/notary-apostille-poa",
    keyword: "apostille power of attorney thailand",
    history: [
      { week: "T+0",  position: null, impressions: 0,   ctr: 0 },
      { week: "T+7",  position: null, impressions: 12,  ctr: 0 },
      { week: "T+14", position: 78,   impressions: 46,  ctr: 0.02 },
      { week: "T+21", position: 54,   impressions: 128, ctr: 0.03 },
      { week: "T+30", position: 38,   impressions: 264, ctr: 0.041 },
    ],
  },
  {
    slug: "dtv-visa-en",
    label: "DTV Visa (EN)",
    url: "/en/services/dtv-visa",
    keyword: "dtv visa thailand",
    history: [
      { week: "T+0",  position: null, impressions: 0,   ctr: 0 },
      { week: "T+7",  position: 92,   impressions: 34,  ctr: 0.01 },
      { week: "T+14", position: 61,   impressions: 145, ctr: 0.024 },
      { week: "T+21", position: 42,   impressions: 388, ctr: 0.037 },
      { week: "T+30", position: 29,   impressions: 712, ctr: 0.052 },
    ],
  },
  {
    slug: "mfa-consular",
    label: "MFA Consular",
    url: "/services/mfa-consular",
    keyword: "mfa consular legalization thailand",
    history: [
      { week: "T+0",  position: 47,   impressions: 210, ctr: 0.028 },
      { week: "T+7",  position: 41,   impressions: 268, ctr: 0.031 },
      { week: "T+14", position: 33,   impressions: 356, ctr: 0.038 },
      { week: "T+21", position: 27,   impressions: 489, ctr: 0.044 },
      { week: "T+30", position: 22,   impressions: 621, ctr: 0.051 },
    ],
  },
];

function delta(history: Snapshot[]): string {
  const first = history.find((h) => h.position != null)?.position;
  const last = history[history.length - 1].position;
  if (first == null || last == null) return "—";
  const d = first - last;
  return d > 0 ? `▲ +${d}` : d < 0 ? `▼ ${d}` : "= 0";
}

function RankDashboard() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-6xl px-4 py-10">
        <header className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-wide text-gold">Internal · Sprint 2</p>
          <h1 className="mt-2 text-3xl font-bold text-foreground">Rank Dashboard — T+30 Quick Wins</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            ติดตามอันดับ 3 หน้าที่มีศักยภาพสูงสุดรายสัปดาห์จนถึง T+30. ข้อมูลจาก Google Search Console
            (manual snapshot; อัตโนมัติเมื่อเชื่อม GSC OAuth เสร็จ).
          </p>
        </header>

        <section className="mb-8 grid gap-4 md:grid-cols-3">
          {PAGES.map((p) => {
            const last = p.history[p.history.length - 1];
            return (
              <div key={p.slug} className="rounded-lg border border-border bg-card p-5">
                <p className="text-xs text-muted-foreground">{p.keyword}</p>
                <h2 className="mt-1 text-lg font-semibold text-foreground">{p.label}</h2>
                <div className="mt-3 flex items-baseline gap-3">
                  <span className="text-3xl font-bold text-gold">
                    {last.position ?? "—"}
                  </span>
                  <span className="text-xs font-medium text-muted-foreground">{delta(p.history)}</span>
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <dt className="text-muted-foreground">Impressions</dt>
                    <dd className="font-semibold text-foreground">{last.impressions.toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">CTR</dt>
                    <dd className="font-semibold text-foreground">{(last.ctr * 100).toFixed(2)}%</dd>
                  </div>
                </dl>
                <a href={p.url} className="mt-3 inline-block text-xs text-gold hover:underline">
                  {p.url} →
                </a>
              </div>
            );
          })}
        </section>

        <ClientOnly fallback={CHART_PLACEHOLDER}>
          <Suspense fallback={CHART_PLACEHOLDER}>
            <RankTrendCharts
              series={[
                { label: PAGES[0].label, color: "#d4a437", points: PAGES[0].history },
                { label: PAGES[1].label, color: "#0f1b3d", points: PAGES[1].history },
                { label: PAGES[2].label, color: "#6b7280", points: PAGES[2].history },
              ]}
            />
          </Suspense>
        </ClientOnly>

        <section className="mt-8 rounded-lg border border-border bg-muted/30 p-5 text-sm text-muted-foreground">
          <h3 className="font-semibold text-foreground">อัพเดทข้อมูลอย่างไร</h3>
          <ol className="mt-2 list-decimal space-y-1 pl-5">
            <li>ทุกวันจันทร์: เปิด Google Search Console → Performance → filter 3 URLs ด้านบน</li>
            <li>แก้ค่า <code>PAGES[i].history</code> ในไฟล์ <code>src/routes/tools/rank-dashboard.tsx</code></li>
            <li>Commit → deploy อัตโนมัติ</li>
            <li>เมื่อเชื่อม GSC OAuth เสร็จ ระบบจะดึงอัตโนมัติผ่าน <code>/api/public/rank-snapshot</code></li>
          </ol>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}